<template>
  <div class="add-purchase-order-body fade-in">
    <!-- GLOBAL LOADER -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading Purchase Order...</p>
      </div>
    </transition>

    <div v-if="!loading">
      <!-- HEADER -->
      <div class="section-header">
        <div class="title-area">
          <h2>Create Purchase Order</h2>
          <p>Record vendor details, line items, and manage procurement efficiently.</p>
        </div>
        <!-- <button class="btn-help"><i class="bx bx-help-circle"></i> Help</button> -->
      </div>

      <!-- BODY -->
      <div class="po-layout">
        <!-- LEFT MAIN -->
        <div class="main-pane">
          <!-- VENDOR INFO -->
          <div class="card-section animate-in">
            <h3><i class="bx bx-user"></i> Vendor Information</h3>
            <div class="form-grid">
              <div class="form-group full">
                <label>Select Vendor</label>
                <div class="vendor-select" style="display: flex;">
                  <select v-model="form.vendor">
                    <option value="">Choose Vendor</option>
                    <option v-for="v in vendors" :key="v.user_id" :value="v.user_id">
                      {{ v.fname }} {{ v.lname }}
                    </option>
                  </select>
                  <button class="btn-outline" @click="showAddVendorModal = true">
                    <i class="bx bx-plus"></i> Add New
                  </button>
                </div>
              </div>
            </div>
            <br>
            <transition name="fade-slide" >
              <div v-if="selectedVendor && selectedVendor.email" class="vendor-preview">
                <p><b>Email:</b> {{ selectedVendor.email }}</p>
                <p><b>Phone:</b> {{ selectedVendor.mobile }}</p>
                <p><b>Address:</b> {{ selectedVendor.address_line1 }}</p>
              </div>
            </transition>
          </div>

          <!-- ORDER DETAILS -->
          <div class="card-section animate-in">
            <h3><i class="bx bx-receipt"></i> Order Details</h3>
            <div class="form-grid two-col">
              <div class="form-group"><label>PO Number</label><input v-model="form.poNumber" readonly /></div>
              <div class="form-group"><label>Order Date</label><input type="date" v-model="form.orderDate" /></div>
              <div class="form-group"><label>Expected Delivery</label><input type="date" v-model="form.deliveryDate" /></div>
              <div class="form-group">
                <label>Status</label>
                <select v-model="form.status">
                  <option>Draft</option>
                  <option>Sent</option>
                  <option>Received</option>
                </select>
              </div>
            </div>
          </div>

          <!-- ITEMS -->
          <div class="card-section animate-in">
            <div class="section-header-mini">
              <h3><i class="bx bx-list-plus"></i> Order Items</h3>
              <button class="btn-black-sm" @click="addItemNow"><i class="bx bx-plus"></i> Add Item</button>
            </div>

            <transition-group name="fade-slide" tag="div" style="margin-top: 1rem;">
              <div v-for="(item, i) in form.items" :key="i" class="item-row">
                <div class="custom-select">
                  <select  v-model="item.name" @change="handleItemSelect($event, i)">
                    <option value="">Select Product</option>
                    <option v-for="p in products" :key="p.item_id" :value="p.item_name">
                      {{ p.item_name }}
                    </option>
                  </select>
                </div>
                <input v-model.number="item.qty" type="number" placeholder="Qty" />
                <input v-model.number="item.rate" type="number" placeholder="Rate ($)" />
                <span class="amount">${{ (item.qty * item.rate).toFixed(2) }}</span>
                <button class="btn-remove" @click="removeItem(i)"><i class="bx bx-x"></i></button>
              </div>
            </transition-group>

            <div class="add-product-row">
              <button class="btn-outline-sm" @click="showAddProductModal = true">
                <i class="bx bx-plus-circle"></i> Add New Product
              </button>
            </div>
          </div>

          <!-- SUMMARY -->
          <div class="card-section summary animate-in">
            <div class="summary-row"><span>Subtotal</span><strong>${{ subtotal.toFixed(2) }}</strong></div>
            <div class="summary-row"><span>Tax (10%)</span><strong>${{ tax.toFixed(2) }}</strong></div>
            <div class="summary-row total"><span>Total</span><strong>${{ total.toFixed(2) }}</strong></div>
          </div>

          <!-- NOTES -->
          <div class="card-section animate-in">
            <h3><i class="bx bx-edit"></i> Notes</h3>
            <textarea v-model="form.notes" placeholder="Add vendor or internal notes..." rows="3"></textarea>
          </div>
        </div>

        <!-- RIGHT SIDEBAR -->
        <div class="side-pane">
          <div class="card-section sticky animate-in">
            <button class="btn-primary full" @click="submitPurchaseOrder('Draft')" style="width: 100%;">
              <i class="bx bx-paper-plane"></i> Send Purchase Order
            </button>
            <div class="divider"><span>or</span></div>

            <h4>Schedule Order</h4>
            <div class="form-group"><label>Date</label><input type="date" v-model="schedule.date" /></div>
            <div class="form-group"><label>Time</label><input type="time" v-model="schedule.time" /></div>
            <button class="btn-black full"  @click="submitPurchaseOrder('Scheduled')" style="margin-top: 1rem; width: 100%;"><i class="bx bx-calendar-check"></i> Schedule Now</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALS (PRODUCT + VENDOR) -->
    <transition name="fade-slide">
      <div v-if="showAddProductModal" class="modal-overlay" @click.self="showAddProductModal = false">
        <div class="modal-card">
          <h3><i class="bx bx-box"></i> Add New Product</h3>
          <div class="form-group"><label>Product Name</label><input v-model="newProduct.item_name" /></div>
          <div class="form-group"><label>Rate ($)</label><input v-model.number="newProduct.item_price" type="number" /></div>
          <div class="modal-actions">
            <button class="btn-primary" @click="addNewProduct">Add</button>
            <button class="btn-outline" @click="showAddProductModal = false">Cancel</button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade-slide">
      <div v-if="showAddVendorModal" class="modal-overlay" @click.self="showAddVendorModal = false">
        <div class="modal-card">
          <h3><i class="bx bx-user-plus"></i> Add New Vendor</h3>
          <div class="form-group"><label>First Name</label><input v-model="newVendor.fname" /></div>
          <div class="form-group"><label>Last Name</label><input v-model="newVendor.lname" /></div>
          <div class="form-group"><label>Email</label><input v-model="newVendor.email" /></div>
          <div class="form-group"><label>Mobile</label><input v-model="newVendor.mobile" /></div>
          <div class="form-group"><label>Address</label><textarea v-model="newVendor.address"></textarea></div>
          <div class="modal-actions">
            <button class="btn-primary" @click="addNewVendor">Add</button>
            <button class="btn-outline" @click="showAddVendorModal = false">Cancel</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { useToast } from "vue-toastification";
import { createInvoice, getInvoiceById, getTableRowCount } from "@/apis/invoiceapis";
import { getItems, addItem } from "@/apis/itemapis";
import { getClients as getVendors, addClient as addVendor } from "@/apis/clientapis";

const toast = useToast();
const loading = ref(true);

const form = ref({
  id: "",
  vendor: "",
  poNumber: "",
  orderDate: "",
  deliveryDate: "",
  status: "Draft",
  notes: "",

  // 🔥 GLOBAL FIELDS (NEW)
  globalTaxType: "none",
  globalTaxValue: parseFloat(0),

  globalDiscountType: "none",
  globalDiscountValue: parseFloat(0),

  otherAmount: parseFloat(0),
  finalTotal: parseFloat(0),

  items: [{ name: "", qty: 1, rate: 0 }],
});

const schedule = ref({
  date: "",
  time: "",
});
const vendors = ref([]);
const products = ref([]);
const showAddProductModal = ref(false);
const showAddVendorModal = ref(false);

const newProduct = ref({ item_name: "", item_price: 0 });
const newVendor = ref({ fname: "", lname: "", email: "", mobile: "", address: "" });

const selectedVendor = computed(() =>
  vendors.value.find((v) => v.user_id === form.value.vendor)
);

const subtotal = computed(() =>
  form.value.items.reduce((sum, i) => sum + i.qty * i.rate, 0)
);
const tax = computed(() => subtotal.value * 0.1);
const total = computed(() => subtotal.value + tax.value);

const addItemNow = () => form.value.items.push({ name: "", qty: 1, rate: 0 });
const removeItem = (i) => form.value.items.splice(i, 1);
const handleItemSelect = (e, i) => {
  const sel = products.value.find((p) => p.item_name === e.target.value);
  if (sel) form.value.items[i].rate = parseFloat(sel.item_unit_price || sel.item_price || 0);
};

async function loadVendors() {
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getVendors(userData.user_id + "", "", 0, "");
  if (res?.status) vendors.value = res.records;
}

async function loadProducts() {
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getItems(0, userData.user_id + "", "");
  if (res?.status) products.value = res.records;
}

const addNewProduct = async () => {
  if (!newProduct.value.item_name.trim()) return toast.warning("Enter product name");
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await addItem({
    item_name: newProduct.value.item_name,
    item_price: newProduct.value.item_price,
    item_by: userData.user_id,
  });
  if (res?.status) {
    toast.success("Product added");
    await loadProducts();
    showAddProductModal.value = false;
  }
};

const addNewVendor = async () => {
  if (!newVendor.value.fname || !newVendor.value.email) return toast.warning("Name & email required");
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await addVendor({ ...newVendor.value, user_cre_by: userData.user_id });
  if (res?.status) {
    toast.success("Vendor added");
    await loadVendors();
    showAddVendorModal.value = false;
  }
};

const validateInvoice = () => {
  if (!form.value.vendor) return "Please select a vendor.";
  if (!form.value.poNumber.trim()) return "Purchase order number is required.";
  if (!form.value.orderDate) return "Purchase order date is required.";
  if (!form.value.deliveryDate) return "Due date is required.";
  if (!form.value.items.length) return "Please add at least one item.";

  for (const item of form.value.items) {
    if (!item.name) return "Each item must have a name.";
    if (!item.qty || item.qty <= 0) return "Quantity must be greater than zero.";
    if (isNaN(item.rate)) return "Item rate must be a valid number.";
  }

  if (total.value <= 0) return "Total amount must be greater than zero.";

  return null;
};


const submitPurchaseOrder = async (status1='Draft') => {

  const error = validateInvoice();
  if (error) {
    toast.warning(error);
    return;
  }

   if (status1 === "Scheduled") {
    if (!schedule.value.date || !schedule.value.time) {
      toast.warning("Please select both schedule date and time before scheduling.");
      return;
    }

    const now = new Date();
    const scheduledDateTime = new Date(`${schedule.value.date}T${schedule.value.time}`);

    if (scheduledDateTime <= now) {
      toast.warning("Schedule date and time must be in the future.");
      return;
    }
  }

  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const formattedItems = form.value.items.map((it) => ({
    name: it.name,
    qty: it.qty,
    rate: it.rate,
  }));

  const payload = {
  po_id: form.value.id,
  invoice_number: form.value.poNumber,
  invoice_client_id: form.value.vendor,
  invoice_date: form.value.orderDate,
  invoice_due_date: form.value.deliveryDate,
  invoice_status: status1 + "",
  invoice_notes: form.value.notes,

  // 🔥 NEW GLOBAL FIELDS
  invoice_global_tax_type: form.value.globalTaxType,
  invoice_global_tax_value: form.value.globalTaxValue,

  invoice_global_discount_type: form.value.globalDiscountType,
  invoice_global_discount_value: form.value.globalDiscountValue,

  invoice_other_amount: form.value.otherAmount,
  invoice_final_total: total.value, // always send final total

  // BASIC TOTALS
  invoice_subtotal: subtotal.value,
  invoice_tax_amount: tax.value,
  invoice_total_amount: total.value,

  invoice_created_by: userData.user_id,

  // SCHEDULE VALUES
  schedule_date: schedule.value.date,
  schedule_time: schedule.value.time,

  items: formattedItems,
};


  const res = await createInvoice("purchaseorder", payload);
  if (res?.status) {
    toast.success("Purchase Order saved!");
    window.location.href = "/invoices?type=purchaseorder";
  } else toast.error("Failed to save");
};

onMounted(async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const poId = urlParams.get("id");

    await Promise.all([loadVendors(), loadProducts()]);

    if (poId) {
      const res = await getInvoiceById("purchaseorder", poId);
      if (res?.status && res.record) {
        const po = res.record;
        form.value = {
        id: poId,
        poNumber: po.po_number || "",
        vendor: po.po_vendor_id || "",
        orderDate: po.po_order_date || "",
        deliveryDate: po.po_delivery_date || "",
        status: po.po_status || "Draft",
        notes: po.po_notes || "",

        // 🔥 NEW GLOBAL FIELDS
        globalTaxType: po.po_global_tax_type || "none",
        globalTaxValue: parseFloat(po.po_global_tax_value || 0),

        globalDiscountType: po.po_global_discount_type || "none",
        globalDiscountValue: parseFloat(po.po_global_discount_value || 0),

        otherAmount: parseFloat(po.po_other_amount || 0),
        finalTotal: parseFloat(po.po_final_total || 0),

        // 🔥 ITEMS
        items: po.items?.map((it) => ({
          name: it.po_item_name,
          qty: parseFloat(it.po_item_qty || 1),
          rate: parseFloat(it.po_item_rate || 0),
        })) || [{ name: "", qty: 1, rate: 0 }],
      };

      // schedule
      schedule.value.date = po.po_schedule_date || "";
      schedule.value.time = po.po_schedule_time || "";

      }
    } else {
      const countRes = await getTableRowCount("purchaseorder");
      if (countRes.status)
        form.value.poNumber = `PO-${String(countRes.total_rows + 1).padStart(4, "0")}`;
    }
  } catch (err) {
    console.error("Init error:", err);
  } finally {
    await nextTick();
    loading.value = false;
  }
});
</script>

<style scoped>
.add-purchase-order-body {
  background: #fff;
  border-radius: 1.5rem;
  padding: 2rem 2.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
}

/* ===== SECTION HEADER ===== */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
}

.section-header h2 {
  font-size: 1.9rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.3rem;
}

.section-header p {
  color: #6b7280;
  font-size: 0.95rem;
}

.btn-help {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  border: none;
  padding: 0.7rem 1.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.25s ease;
}

.btn-help:hover {
  background: #1e293b;
}

/* ===== LAYOUT ===== */
.po-layout {
  display: flex;
  gap: 2.2rem;
}

.main-pane {
  width: 68%;
}

.side-pane {
  width: 32%;
}

/* ===== CARD SECTIONS ===== */
.card-section {
  background: #f9fafb;
  border-radius: 1rem;
  padding: 1.8rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}

.card-section:hover {
  box-shadow: 0 6px 14px rgba(17, 24, 39, 0.06);
}

.card-section h3 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-header-mini {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ===== FORM GRID ===== */
.form-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.form-grid.two-col .form-group {
  flex: 1 1 calc(50% - 1rem);
}

.form-group.full {
  flex: 1 1 100%;
}

label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 0.3rem;
  display: block;
}

input,
select,
textarea {
  width: 100%;
  padding: 0.8rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #fff;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #111827; /* teal accent */
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
  outline: none;
}

textarea {
  resize: none;
}

/* ===== ITEM ROWS ===== */
.item-row {
  display: grid;
  grid-template-columns: 2fr 0.8fr 0.8fr 1fr 40px;
  gap: 0.8rem;
  align-items: center;
  margin-bottom: 0.8rem;
  padding: 0.8rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.amount {
  text-align: right;
  font-weight: 600;
  color: #111827;
}

.btn-remove {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 1.3rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.btn-remove:hover {
  transform: scale(1.15);
}

/* ===== CUSTOM SELECT ===== */
.custom-select select {
  width: 100%;
  padding: 0.7rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  font-size: 0.9rem;
  color: #111827;
  transition: all 0.25s ease;
  appearance: none;
  cursor: pointer;
}

.custom-select select:focus {
  border-color: #111827;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
  outline: none;
}

/* ===== ADD PRODUCT BUTTON ===== */
.add-product-row {
  margin-top: 1rem;
  text-align: right;
}

.btn-outline-sm {
  background: #fff;
  color: #111827;
  border: 1px solid #111827;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-outline-sm:hover {
  background: #111827;
  color: #fff;
  transform: translateY(-1px);
}

/* ===== SUMMARY ===== */
.summary {
  background: linear-gradient(135deg, #111827, #115e59);
  color: #fff;
  border-radius: 1rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}

.summary-row.total {
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  margin-top: 0.8rem;
  padding-top: 0.8rem;
}

/* ===== BUTTONS ===== */
.btn-primary,
.btn-outline,
.btn-black,
.btn-black-sm {
  transition: all 0.3s ease;
  font-weight: 500;
  width: 30%;
}

.btn-primary {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.4rem;
}

.btn-primary:hover {
  background: #111827;
  box-shadow: 0 3px 8px rgba(13, 148, 136, 0.25);
}

.btn-black {
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.8rem 1.4rem;
}

.btn-black-sm {
  background: #111827;
  color: #fff;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
}

.btn-outline {
  background: #fff;
  border: 1px solid #111827;
  color: #111827;
  border-radius: 10px;
  padding: 0.7rem 1.4rem;
}

.btn-outline:hover {
  background: #111827;
  color: #fff;
}

/* ===== DIVIDER ===== */
.divider {
  position: relative;
  text-align: center;
  color: #9ca3af;
  margin: 1.2rem 0;
}

.divider::before,
.divider::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: #e5e7eb;
}

.divider::before {
  left: 0;
}
.divider::after {
  right: 0;
}

/* ===== SIDEBAR ===== */

.sticky {
  position: sticky;
  top: 110px;   /* below page header */
  z-index: 10;
}

/* ===== SETTINGS DRAWER ===== */
.settings-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #fff;
  box-shadow: -6px 0 20px rgba(0, 0, 0, 0.1);
  border-radius: 1rem 0 0 1rem;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 2rem;
  z-index: 1000;
  overflow-y: auto;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.drawer-content section {
  margin-bottom: 1.8rem;
}

.drawer-content h4 {
  margin-bottom: 0.4rem;
  color: #111827;
}

.drawer-content textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.7rem;
  font-size: 0.9rem;
  resize: none;
}

.close-btn {
  border: none;
  background: none;
  font-size: 1.6rem;
  cursor: pointer;
}

/* ===== MODAL ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-card {
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.3s ease;
}

.modal-card h3 {
  font-size: 1.2rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.modal-card .form-group {
  margin-bottom: 1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}

/* ===== ANIMATIONS ===== */
.fade-in {
  animation: fadeIn 0.6s ease;
}

.fade-slide-enter-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
  .po-layout {
    flex-direction: column;
  }

  .main-pane,
  .side-pane {
    width: 100%;
  }

  .settings-drawer {
    width: 100%;
    border-radius: 1rem 1rem 0 0;
  }

  .item-row {
    grid-template-columns: 1fr 1fr;
  }

  .amount {
    text-align: left;
  }
}

/* ===== MICRO INTERACTIONS ===== */
button:active {
  transform: scale(0.97);
}

.card-section:hover h3 i {
  color: #111827;
  transition: color 0.3s ease;
}
/* ===== GLOBAL ROOT VARIABLES ===== */
:root {
  --primary: #111827;
  --primary-dark: #111827;
  --border: #e5e7eb;
  --muted: #6b7280;
  --text: #111827;
  --radius: 12px;
  --card: #ffffff;
}

/* ===== FORM ALIGNMENT FIXES ===== */
.form-group {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

.form-group input,
.form-group select {
  margin-top: 0.3rem;
}

/* For inline form items like switches */
.switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

/* ===== LABEL FIX ===== */
label {
  display: block;
  font-weight: 600;
  color: var(--text);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

/* ===== PLACEHOLDER STYLE ===== */
input::placeholder,
textarea::placeholder {
  color: #9ca3af;
}

/* ===== CUSTOM SCROLLBAR (Drawer + Modal) ===== */
.settings-drawer::-webkit-scrollbar,
.modal-overlay::-webkit-scrollbar {
  width: 8px;
}

.settings-drawer::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
}

.settings-drawer::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* ===== SETTINGS DRAWER ANIMATION ===== */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition: transform 0.4s ease, opacity 0.3s ease;
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* ===== DRAWER SECTIONS ===== */
.drawer-content p {
  color: var(--muted);
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
  line-height: 1.5;
}

.drawer-content select,
.drawer-content input[type="color"] {
  margin-top: 0.5rem;
}

/* ===== MODAL IMPROVEMENTS ===== */
.modal-card input,
.modal-card select {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.8rem;
  font-size: 0.9rem;
  transition: border 0.3s ease, box-shadow 0.3s ease;
}

.modal-card input:focus,
.modal-card select:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
  outline: none;
}

.modal-actions button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

/* ===== MODAL FADE-IN ===== */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ===== ALIGNMENT FIX FOR AMOUNT COLUMN ===== */
.amount {
  text-align: right;
  min-width: 80px;
}

/* ===== TOOLTIP / HOVER MICRO EFFECT ===== */
.btn-outline-sm i,
.btn-black-sm i,
.btn-primary i {
  font-size: 1rem;
  transition: transform 0.3s ease;
}

.btn-outline-sm:hover i,
.btn-black-sm:hover i,
.btn-primary:hover i {
  transform: translateX(3px);
}

/* ===== CARD HEADER UX POLISH ===== */
.card-section h3 i {
  color: var(--muted);
  transition: color 0.3s ease;
}

.card-section:hover h3 i {
  color: var(--primary);
}

/* ===== SUMMARY SECTION ===== */
.summary {
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: #fff;
  border-radius: var(--radius);
  padding: 1.5rem;
  font-size: 0.95rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.4rem 0;
}

.summary-row strong {
  font-weight: 700;
  color: #fff;
}

/* ===== SIDEBAR FIXES ===== */
.side-pane .card-section h4 {
  font-size: 1rem;
  color: var(--text);
  margin-bottom: 0.4rem;
}

/* ===== SIDEBAR BUTTON ALIGNMENT ===== */
.side-pane button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

/* ===== DRAWER CLOSE BUTTON ===== */
.close-btn {
  background: none;
  border: none;
  color: var(--muted);
  font-size: 1.6rem;
  cursor: pointer;
  transition: transform 0.3s ease, color 0.3s ease;
}
.close-btn:hover {
  color: var(--primary);
  transform: rotate(90deg);
}

/* ===== RESPONSIVE ENHANCEMENTS ===== */
@media (max-width: 992px) {
  .po-layout {
    flex-direction: column;
  }

  .main-pane,
  .side-pane {
    width: 100%;
  }

  .item-row {
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
  }

  .amount {
    text-align: left;
  }

  .summary {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .add-purchase-order-body {
    padding: 1.5rem;
  }
}

/* ===== BUTTON MICRO EFFECTS ===== */
button {
  transition: all 0.25s ease;
}
button:active {
  transform: scale(0.97);
}
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ===== HIGHLIGHT FOR PURCHASE CONTEXT ===== */
.po-highlight {
  background: var(--primary-bg, #f0fdfa);
  border: 1px solid #99f6e4;
  border-radius: 10px;
  padding: 0.8rem 1rem;
  color: #111827;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* ===== TABLE-LIKE ITEM VIEW (optional) ===== */
.item-row input[type="number"]::-webkit-inner-spin-button {
  opacity: 0.5;
}

/* ===== TOGGLE / SWITCH STYLING ===== */
.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 22px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--primary);
}
input:checked + .slider:before {
  transform: translateX(18px);
}

/* ===== DRAWER FOOTER SPACING ===== */
.drawer-content section:last-child {
  margin-bottom: 2rem;
}
/* ===== FORM ALIGNMENT FIXES ===== */

/* Make all inputs, selects, and buttons line up nicely in flex/grid forms */
.form-grid,
.vendor-select {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
}

/* Ensure all labels align consistently */
.form-group {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1 1 45%;
  min-width: 200px;
}

/* Remove extra vertical gaps between stacked form groups */
.form-group label {
  margin-bottom: 0.3rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
}

/* Normalize spacing between inputs and selects */
.form-group input,
.form-group select {
  margin-top: 0;
  height: 42px;
  line-height: 1.4;
}

/* Vendor Select Section - keep button aligned to dropdown */
.vendor-select {
  align-items: flex-end;
  justify-content: flex-start;
}

.vendor-select select {
  flex: 1;
  min-width: 220px;
}

.vendor-select .btn-outline {
  align-self: flex-end;
  padding: 0.6rem 1.2rem;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Two-column grid fix */
.form-grid.two-col {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.2rem;
}

/* Fix label/input misalignment for compact forms */
.form-group.full,
.form-group.half {
  width: 100%;
}

/* Consistent spacing in section headers with filters or buttons */
.section-header,
.section-header-mini {
  align-items: center;
}

.section-header-mini h3 {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

/* Make buttons align perfectly with inputs in grids */
.btn-black-sm,
.btn-outline-sm,
.btn-primary,
.btn-outline {
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

/* Fix alignment on smaller screens */
@media (max-width: 768px) {
  .form-grid,
  .vendor-select {
    flex-direction: column;
    align-items: stretch;
  }

  .vendor-select .btn-outline {
    width: 100%;
  }
}
/* === FILTER BAR ALIGNMENT FIX === */
.filters {
  display: flex;
  align-items: flex-end; /* bottom align for clean baseline */
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-bottom: 0.8rem;
}

.filters > * {
  margin: 0;
}

/* Search Box Consistency */
.search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0 0.8rem;
  height: 42px;
  min-width: 220px;
  flex: 1;
}

.search-box i {
  color: var(--muted);
  font-size: 1.2rem;
}

.search-box input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.9rem;
  padding-left: 0.5rem;
}

/* Filter Buttons Consistency */
.filter-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-btn {
  height: 42px;
  padding: 0 1rem;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: #f9fafb;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
}

.filter-btn.active,
.filter-btn:hover {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

/* Dropdown Consistency */
.select-theme {
  height: 42px;
  padding: 0 1rem;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: #fff;
  font-size: 0.9rem;
  cursor: pointer;
}

/* Add Button Alignment (Top Right) */
.add-btn-dropdown {
  align-self: flex-end;
}

.add-btn-dropdown .btn-primary {
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  font-size: 0.9rem;
  border-radius: 8px;
}

/* Small Viewport Fix */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  .add-btn-dropdown {
    align-self: stretch;
  }
}
/* === 📱 MOBILE RESPONSIVE PATCH (for Purchase Order Screen) === */
@media (max-width: 768px) {
  /* === GENERAL CONTAINER === */
  .add-purchase-order-body {
    padding: 1.2rem !important;
    border-radius: 1rem !important;
  }

  /* === HEADER === */
  .section-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem !important;
  }

  .section-header h2 {
    font-size: 1.4rem !important;
  }

  .section-header p {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
  }

  .btn-help {
    width: 100% !important;
    justify-content: center !important;
    font-size: 0.9rem !important;
    padding: 0.6rem 1rem !important;
  }

  /* === LAYOUT === */
  .po-layout {
    flex-direction: column !important;
    gap: 1.2rem !important;
  }

  .main-pane,
  .side-pane {
    width: 100% !important;
  }

  /* === CARD SECTION === */
  .card-section {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  .card-section h3 {
    font-size: 1rem !important;
    margin-bottom: 0.8rem !important;
  }

  /* === FORM GRID === */
  .form-grid.two-col {
    grid-template-columns: 1fr !important;
  }

  .form-group {
    width: 100% !important;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    font-size: 0.9rem !important;
    padding: 0.7rem 0.9rem !important;
  }

  /* === VENDOR SELECT === */
  .vendor-select {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.6rem !important;
  }

  .vendor-select select {
    width: 100% !important;
  }

  .vendor-select .btn-outline {
    width: 100% !important;
  }

  /* === LINE ITEMS === */
  .item-row {
    grid-template-columns: 1fr 1fr !important;
    gap: 0.6rem !important;
    padding: 0.8rem !important;
    border-radius: 0.8rem !important;
  }

  .item-row input,
  .item-row select {
    font-size: 0.9rem !important;
  }

  .item-row .amount {
    text-align: left !important;
    font-size: 0.9rem !important;
  }

  .btn-remove {
    font-size: 1rem !important;
    justify-self: flex-end !important;
  }

  /* === ADD PRODUCT BUTTON === */
  .add-product-row {
    justify-content: center !important;
    text-align: center !important;
  }

  .btn-outline-sm {
    width: 100% !important;
  }

  /* === SUMMARY === */
  .summary {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  .summary-row {
    font-size: 0.85rem !important;
  }

  .summary-row.total strong {
    font-size: 1rem !important;
  }

  /* === NOTES === */
  textarea {
    font-size: 0.9rem !important;
    padding: 0.7rem !important;
  }

  /* === SIDEBAR BUTTONS === */
  .btn-primary,
  .btn-outline,
  .btn-black {
    width: 100% !important;
    justify-content: center !important;
    font-size: 0.95rem !important;
    padding: 0.8rem !important;
  }

  .side-pane .card-section button {
    margin-top: 0.6rem !important;
  }

  /* === SETTINGS DRAWER === */
  .settings-drawer {
    width: 100% !important;
    height: 90vh !important;
    border-radius: 1rem 1rem 0 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    padding: 1.4rem !important;
  }

  .settings-drawer::-webkit-scrollbar {
    width: 6px;
  }

  .settings-drawer::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
  }

  .drawer-header h3 {
    font-size: 1.1rem !important;
  }

  .drawer-content section {
    padding: 0.8rem 0 !important;
  }

  /* === DRAWER TEXTAREA === */
  .drawer-content textarea {
    font-size: 0.9rem !important;
  }

  /* === DIVIDER === */
  .divider {
    margin: 1rem 0 !important;
  }

  /* === SMALL FIX FOR BUTTON ICONS === */
  .btn-primary i,
  .btn-outline i,
  .btn-black i {
    font-size: 1rem !important;
  }
}

/* === 📱 SMALL DEVICES (≤480px) === */
@media (max-width: 480px) {
  .section-header h2 {
    font-size: 1.2rem !important;
  }

  .section-header p {
    font-size: 0.85rem !important;
  }

  .card-section {
    padding: 1rem !important;
  }

  .item-row {
    grid-template-columns: 1fr !important;
    gap: 0.5rem !important;
  }

  .item-row input,
  .item-row select {
    width: 100% !important;
  }

  .btn-help {
    width: 100% !important;
  }

  .summary {
    padding: 1rem !important;
  }

  .settings-drawer {
    height: 85vh !important;
  }

  .btn-outline-sm,
  .btn-black-sm {
    width: 100% !important;
  }

  .modal-card {
    width: 95% !important;
    padding: 1.2rem !important;
  }

  .modal-card h3 {
    font-size: 1rem !important;
  }

  .modal-actions button {
    width: 100% !important;
  }
}
/* === 📱 PATCH: Fix 'Add Item' Button Alignment in Mobile View === */
@media (max-width: 768px) {
  /* Wraps the "Order Items" heading and Add button nicely */
  .section-header-mini {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.6rem !important;
  }

  /* Make "Add Item" button span full width for better tap area */
  .section-header-mini .btn-black-sm {
    width: 100% !important;
    justify-content: center !important;
    font-size: 0.9rem !important;
    padding: 0.7rem !important;
  }

  /* Add spacing below the button */
  .section-header-mini .btn-black-sm + * {
    margin-top: 0.6rem !important;
  }

  /* Compact heading */
  .section-header-mini h3 {
    font-size: 1rem !important;
    margin: 0 !important;
  }
}

/* For smaller devices (phones) */
@media (max-width: 480px) {
  .section-header-mini {
    gap: 0.4rem !important;
  }

  .section-header-mini h3 {
    font-size: 0.95rem !important;
  }

  .section-header-mini .btn-black-sm {
    width: 100% !important;
    font-size: 0.85rem !important;
    padding: 0.65rem 0.8rem !important;
  }
}
/* Loader fix */
.global-loader {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(3px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #ddd;
  border-top: 4px solid #111827;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

</style>